// src/services/goalPointService.js
//
// Loads what the rule-points engine needs (goalRules.js) and records an
// admin's decision. Approving is ONE transaction that:
//   1. re-reads the running totals and refuses if anyone decided meanwhile,
//   2. re-reads the coordinator's monthly and weekly counts,
//   3. adds the points to those counts (the same documents and fields the
//      leaderboard's hand edits write: { count, periodId, updatedAt, updatedBy }),
//   4. writes an append-only decision and the new totals.
// Dismissing writes only the decision and totals, so those points aren't
// offered again. Nothing is ever subtracted or deleted.

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  getCoordinatorEnrollmentsPath,
  getCoordinatorScreeningsPath,
  getCoordinatorScreeningsWeeklyPath,
  getCoordinatorWeeklyPath,
  getEnrollmentPeriodPath,
  getGoalPointDecisionsPath,
  getGoalPointTotalsPath,
  getScreeningPeriodPath,
  getScreeningWeeklyPeriodPath,
  getStudiesPath,
  getWeeklyPeriodPath,
  getWeeklyReportsPath,
} from "./firestorePaths";
import { fetchActiveCoordinators } from "./coordinatorService";
import { loadRuleSets } from "./goalRuleService";
import { mergeLeaderboard } from "./goalProgress";

// Where each goal's counts live: monthly + weekly, period docs + per-coordinator docs.
const COUNT_PATHS = {
  enrollments: {
    monthParent: getCoordinatorEnrollmentsPath,
    month: getEnrollmentPeriodPath,
    weekParent: getCoordinatorWeeklyPath,
    week: getWeeklyPeriodPath,
  },
  screenings: {
    monthParent: getCoordinatorScreeningsPath,
    month: getScreeningPeriodPath,
    weekParent: getCoordinatorScreeningsWeeklyPath,
    week: getScreeningWeeklyPeriodPath,
  },
};

/** Everything the approvals panel needs, read in parallel. */
export const loadGoalPointData = async () => {
  const [reportSnap, studySnap, enrollmentSets, screeningSets, totalsSnap, coordinators] = await Promise.all([
    getDocs(query(collection(db, getWeeklyReportsPath()), where("format", "==", 2))),
    getDocs(collection(db, getStudiesPath())),
    loadRuleSets("enrollments"),
    loadRuleSets("screenings"),
    getDocs(collection(db, getGoalPointTotalsPath())),
    fetchActiveCoordinators().catch(() => []),
  ]);
  const totalsById = {};
  totalsSnap.docs.forEach((d) => {
    totalsById[d.id] = d.data();
  });
  return {
    reports: reportSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
    studiesById: new Map(studySnap.docs.map((d) => [d.id, { id: d.id, ...d.data() }])),
    ruleSetsByMetric: { enrollments: enrollmentSets, screenings: screeningSets },
    totalsById,
    roster: coordinators.filter((c) => c.includeInLeaderboard !== false).map((c) => c.name),
  };
};

const countOf = (snap) => (snap?.exists() ? Math.max(Number(snap.data()?.count) || 0, 0) : 0);

/**
 * What approving would change, for the confirm pop-up: the coordinator's
 * monthly and weekly counts before -> after, the circle's total before ->
 * after, and whether that month was already closed.
 */
export const previewDecision = async ({ card, roster }) => {
  const paths = COUNT_PATHS[card.metric];
  const [monthSnap, parentSnap, monthAll, ...weekSnaps] = await Promise.all([
    getDoc(doc(db, paths.month(card.monthId), card.coordinator)),
    getDoc(doc(db, paths.monthParent(), card.monthId)),
    getDocs(collection(db, paths.month(card.monthId))),
    ...Object.keys(card.newWeekly).map((w) => getDoc(doc(db, paths.week(w), card.coordinator))),
  ]);
  const monthBefore = countOf(monthSnap);
  const names = roster && roster.length ? roster : [card.coordinator];
  const { total: circleBefore } = mergeLeaderboard(
    monthAll.docs.map((d) => ({ name: d.id, ...d.data() })),
    names
  );
  return {
    monthBefore,
    monthAfter: monthBefore + card.newPoints,
    circleBefore,
    circleAfter: circleBefore + (names.includes(card.coordinator) ? card.newPoints : 0),
    weeks: Object.entries(card.newWeekly).map(([weekOf, add], i) => {
      const before = countOf(weekSnaps[i]);
      return { weekOf, before, after: before + add };
    }),
    monthClosed: !!(parentSnap.exists() && parentSnap.data()?.closedAt),
  };
};

/**
 * Approve (add to the counts) or dismiss a card's new points.
 * Returns { countBefore, countAfter } on approval, else null.
 */
export const decideGoalPoints = async ({ card, status, by }) => {
  const approve = status === "approved";
  const who = String(by || "").toLowerCase();
  const paths = COUNT_PATHS[card.metric];
  const totalsRef = doc(db, getGoalPointTotalsPath(), card.id);
  const monthRef = doc(db, paths.month(card.monthId), card.coordinator);
  const weekRefs = Object.keys(card.newWeekly).map((w) => [w, doc(db, paths.week(w), card.coordinator)]);
  const decisionRef = doc(collection(db, getGoalPointDecisionsPath()));

  return runTransaction(db, async (tx) => {
    // Reads first (a transaction's rule).
    const totalsSnap = await tx.get(totalsRef);
    const totals = totalsSnap.exists() ? totalsSnap.data() : {};
    const approvedNow = Math.max(Number(totals.approved) || 0, 0);
    const dismissedNow = Math.max(Number(totals.dismissed) || 0, 0);
    if (approvedNow !== card.decidedBefore.approved || dismissedNow !== card.decidedBefore.dismissed) {
      throw new Error("Someone already decided on these points. Refresh to see what's left.");
    }
    let monthBefore = 0;
    const weekBefore = {};
    if (approve) {
      monthBefore = countOf(await tx.get(monthRef));
      for (const [w, ref] of weekRefs) weekBefore[w] = countOf(await tx.get(ref));
    }

    // Writes.
    if (approve) {
      const stamp = { updatedAt: serverTimestamp(), updatedBy: who };
      tx.set(monthRef, { count: monthBefore + card.newPoints, periodId: card.monthId, ...stamp }, { merge: true });
      tx.set(doc(db, paths.monthParent(), card.monthId), { periodId: card.monthId, updatedAt: serverTimestamp() }, { merge: true });
      weekRefs.forEach(([w, ref]) => {
        tx.set(ref, { count: weekBefore[w] + card.newWeekly[w], periodId: w, ...stamp }, { merge: true });
        tx.set(doc(db, paths.weekParent(), w), { periodId: w, updatedAt: serverTimestamp() }, { merge: true });
      });
    }
    const decidedWeekly = { ...(totals.decidedWeekly || {}) };
    Object.entries(card.newWeekly).forEach(([w, add]) => {
      decidedWeekly[w] = (Number(decidedWeekly[w]) || 0) + add;
    });
    tx.set(decisionRef, {
      metric: card.metric,
      monthId: card.monthId,
      coordinator: card.coordinator,
      status: approve ? "approved" : "dismissed",
      points: card.newPoints,
      weekly: card.newWeekly,
      breakdown: card.breakdown.map((b) => ({ ruleId: b.ruleId, text: b.text || "", points: b.points })),
      reportIds: card.reportIds,
      periodStart: card.periodStart,
      periodEnd: card.periodEnd,
      rulesFrom: card.rulesFrom,
      countBefore: approve ? monthBefore : null,
      countAfter: approve ? monthBefore + card.newPoints : null,
      decidedBy: who,
      decidedAt: serverTimestamp(),
    });
    tx.set(totalsRef, {
      metric: card.metric,
      monthId: card.monthId,
      coordinator: card.coordinator,
      approved: approvedNow + (approve ? card.newPoints : 0),
      dismissed: dismissedNow + (approve ? 0 : card.newPoints),
      decidedWeekly,
      updatedAt: serverTimestamp(),
    });
    return approve ? { countBefore: monthBefore, countAfter: monthBefore + card.newPoints } : null;
  });
};
